import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiRepeat, FiChevronRight } from 'react-icons/fi';
import useAuth from '../../hooks/useAuth';
import api from '../../utils/api';

// "Programarea ta" — the returning-client shortcut at the top of the native
// home. Shows the soonest upcoming booking (a reminder) or, if there's none,
// a one-tap "book again" at the last salon. Renders nothing for guests / users
// with no bookings, so a first-time visitor still sees the clean, airy home.
const UPCOMING = ['PENDING', 'CONFIRMED', 'IN_PROGRESS'];

export default function YourBookings() {
  const { user } = useAuth();
  const [state, setState] = useState(undefined); // undefined=loading, null=hide

  useEffect(() => {
    if (!user) { setState(null); return undefined; }
    let cancelled = false;
    api.get('/bookings/my', { params: { limit: 50 } })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.bookings) ? res.data.bookings : [];
        // Guard each item: the API list could carry a null entry or a booking
        // with no date (partial record / mapping gap); without `b && b.date`
        // those would throw on b.status or sort to NaN and scramble the order.
        const upcoming = list
          .filter((b) => b && b.date && !Number.isNaN(Date.parse(b.date)) && UPCOMING.includes(b.status))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        if (upcoming[0]) { setState({ kind: 'upcoming', booking: upcoming[0] }); return; }
        const lastDone = list
          .filter((b) => b && b.date && !Number.isNaN(Date.parse(b.date)) && b.status === 'COMPLETED')
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        setState(lastDone ? { kind: 'rebook', booking: lastDone } : null);
      })
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, [user]);

  if (!state) return null;
  const { kind, booking } = state;
  const salon = booking.salon || {};

  if (kind === 'rebook') {
    return (
      <section className="mx-auto w-full max-w-2xl px-4 pt-4 sm:px-6 lg:px-8">
        <Link
          to={salon.slug ? `/salon/${salon.slug}` : '/'}
          className="flex items-center gap-4 rounded-3xl border border-ink/10 bg-pastel-peach/60 p-4 transition-transform active:scale-[0.99]"
        >
          <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-cream-50 text-primary-600">
            <FiRepeat className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-ink">Programează din nou</div>
            <p className="mt-0.5 truncate text-sm text-ink-600">{salon.name}</p>
          </div>
          <FiChevronRight className="h-5 w-5 flex-shrink-0 text-ink-400" aria-hidden="true" />
        </Link>
      </section>
    );
  }

  const parsedDate = new Date(booking.date);
  const dateLabel = Number.isNaN(parsedDate.getTime())
    ? ''
    : parsedDate.toLocaleDateString('ro-RO', { weekday: 'short', day: 'numeric', month: 'long' });
  return (
    <section className="mx-auto w-full max-w-2xl px-4 pt-4 sm:px-6 lg:px-8">
      <h2 className="mb-2 text-base font-bold text-ink">Programarea ta</h2>
      <Link
        to="/my-bookings"
        className="flex items-center gap-4 rounded-3xl border border-ink/10 bg-cream-50 p-4 transition-transform active:scale-[0.99]"
      >
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-pastel-sky text-pastel-sky-deep">
          <FiCalendar className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-bold capitalize text-ink">{dateLabel}, {booking.startTime}</div>
          <p className="mt-0.5 truncate text-sm text-ink-600">{salon.name}</p>
        </div>
        <FiChevronRight className="h-5 w-5 flex-shrink-0 text-ink-400" aria-hidden="true" />
      </Link>
    </section>
  );
}
